// Utilitários do modelo "Norte" (topo 17 · busca 20 · card 14 · rodapé 14) — a linguagem de layout de um
// portal imobiliário de referência (sans geométrica + serifa só nos títulos de destaque, MONOCROMÁTICO: preto,
// branco e cinzas; tudo RETO no herói — abas quadradas e caixa de busca com botão preto quadrado —, pílulas
// brancas contornadas nos botões, cards brancos de raio 8 com sombra curta, faixas pretas de destaque e rodapé
// preto com títulos em serifa) reproduzida em CSS modules NOSSOS, com as CORES DO TENANT no lugar do preto.
// Só as peças do Norte importam daqui — não é componente compartilhado dos ~300 sites. Reusa (importa, não
// copia) os helpers genéricos do Horizonte/Atelier/Larissa/Internacional.
import type { CSSProperties } from 'react';
import { getContrastRatio } from '../../../utils/FunctionsGlobals';
import { hexParaRgba, misturarCores } from '../horizonte/horizonte';
import { areaFormatada } from '../internacional/internacional';
import { ajustarAte, hexValido, luminancia, serveDeBase } from '../larissa/larissa';

export { contatosDoTenant, hexParaRgba, linkWhatsApp, linkWhatsAppDoTenant, menuDoTenant, mesmaRota, misturarCores, redesDoTenant, useMovimentoReduzido } from '../horizonte/horizonte';
export type { ItemMenu, TelefoneTenant } from '../horizonte/horizonte';
export { fotoOtimizada, fotosDoImovel, nomeDaEmpresa, stripHtml, urlDoImovel } from '../atelier/atelier';
export { precosRotulados, semCaixaAlta, useLogoSemPlaca } from '../larissa/larissa';
export type { PrecoRotulado } from '../larissa/larissa';
export { areaFormatada, enderecoDoImovel, finalidadeDoImovel, semDuplicatas } from '../internacional/internacional';

// Curva das entradas do modelo (desacelera longo no fim) — a mesma dos modelos 8–12.
export const EASE_NORTE = [0.22, 1, 0.36, 1] as const;

/** Sans geométrica (Outfit, OFL) no lugar da sans proprietária da referência; serifa (Newsreader, OFL) nos destaques. */
export const SANS_NORTE = "'Outfit', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
export const SERIFA_NORTE = "'Newsreader', 'Times New Roman', Times, serif";

/**
 * Escala de RESERVA = tokens MEDIDOS no site de referência (getComputedStyle do snapshot de 19/10/2026,
 * /root/deploy-norte-21out/ref/RELATORIO.md): preto das faixas/botão de busca/rodapé, quase-preto dos
 * títulos (rgb 23,23,23), texto das abas (36,36,36), cinza do placeholder (108,108,108) e do rótulo
 * (78,78,78), aba inativa e borda clara (232,232,232), borda das pílulas (173,173,173), caixa de apoio
 * (244,244,244). Entra só onde o tenant não tem cor utilizável.
 */
export const RESERVA_NT = {
    preto: '#000000',
    tinta: '#171717',
    texto: '#242424',
    cinza: '#6c6c6c',
    rotulo: '#4e4e4e',
    aba: '#e8e8e8',
    borda: '#e8e8e8',
    bordaForte: '#adadad',
    apoio: '#f4f4f4',
    branco: '#ffffff',
};

export interface PaletaNorte {
    /** o "preto" do modelo na cor do tenant: botão de busca, pílulas sólidas, faixas de destaque (texto branco ≥ 7:1). */
    base: string;
    /** base mais funda (hover dos botões sólidos). */
    hover: string;
    /** rodapé e faixas mais escuras: base puxada para o preto (texto branco ≥ 12:1). */
    profundo: string;
    /** títulos sobre o branco (quase-preto da referência com pitada da base, ≥ 12:1). */
    tinta: string;
    /** texto corrido e das abas. */
    texto: string;
    /** texto secundário sobre o branco/apoio (≥ 4.5:1). */
    cinza: string;
    /** rótulos pequenos ("Buscar por…"), ≥ 7:1. */
    rotulo: string;
    /** aba inativa do herói (cinza claro) e fundo de chips. */
    aba: string;
    /** bordas claras e divisórias. */
    borda: string;
    /** borda das pílulas contornadas e das setas. */
    bordaForte: string;
    /** caixa de apoio cinza-clarinha (cartão "fale com um corretor"). */
    apoio: string;
    /** texto secundário SOBRE a base/profundo (≥ 4.5:1). */
    sobreBase: string;
}

/**
 * Mapeamento p/ os 300 sites (mesma escolha de cor do paletaLarissa/paletaInternacional): base = a cor MAIS
 * ESCURA do tenant que serve (saturada ou preto/grafite; cor1 em empate, nunca branco/cinza claro), escurecida
 * até texto branco ler ≥ 7:1 — a referência é preto puro (21:1), então a base precisa ficar FUNDA para manter
 * o peso. profundo = base a caminho do preto até ≥ 12:1 (rodapé). Os neutros são os medidos, com 4–8% da base.
 * Sem cor no tenant → o preto da referência.
 */
export const paletaNorte = (site: any): PaletaNorte => {
    const cor1 = hexValido(site?.cor1);
    const cor2 = hexValido(site?.cor2);
    const bases = [cor1, cor2].filter(serveDeBase);
    // a cor2 só toma o lugar da principal quando é CLARAMENTE mais escura
    const bruta = bases.length === 2 && luminancia(cor2) < luminancia(cor1) * 0.6 ? cor2 : bases[0] ?? '';

    const base = bruta ? ajustarAte(bruta, '#000000', '#ffffff', 7) : RESERVA_NT.preto;
    const hover = bruta ? misturarCores(base, '#000000', 0.3) : '#262626';
    const profundo = bruta ? ajustarAte(base, '#000000', '#ffffff', 12) : RESERVA_NT.preto;
    const tinta = bruta ? ajustarAte(misturarCores(RESERVA_NT.tinta, base, 0.08), '#000000', '#ffffff', 12) : RESERVA_NT.tinta;
    const pitada = (cor: string, t: number) => (bruta ? misturarCores(cor, base, t) : cor);
    const apoio = pitada(RESERVA_NT.apoio, 0.03);
    const cinza = (() => {
        const partida = pitada(RESERVA_NT.cinza, 0.06);
        for (let t = 0; t <= 1.0001; t += 0.05) {
            const c = t === 0 ? partida : misturarCores(partida, RESERVA_NT.texto, t);
            if (getContrastRatio(c, apoio) >= 4.5) return c;
        }
        return tinta;
    })();
    const sobreBase = (() => {
        const partida = misturarCores(base, '#ffffff', 0.62);
        for (let t = 0; t <= 1.0001; t += 0.05) {
            const c = t === 0 ? partida : misturarCores(partida, '#ffffff', t);
            if (getContrastRatio(c, base) >= 4.5) return c;
        }
        return '#ffffff';
    })();

    return {
        base,
        hover,
        profundo,
        tinta,
        texto: pitada(RESERVA_NT.texto, 0.04),
        cinza,
        rotulo: pitada(RESERVA_NT.rotulo, 0.05),
        aba: pitada(RESERVA_NT.aba, 0.04),
        borda: pitada(RESERVA_NT.borda, 0.04),
        bordaForte: pitada(RESERVA_NT.bordaForte, 0.06),
        apoio,
        sobreBase,
    };
};

/** Variáveis CSS (--nt-*) que os módulos do Norte consomem; o Builder pode sobrescrevê-las por CSS custom. */
export const varsNorte = (site: any): CSSProperties => {
    const p = paletaNorte(site);
    return {
        '--nt-base': p.base,
        '--nt-hover': p.hover,
        '--nt-profundo': p.profundo,
        '--nt-tinta': p.tinta,
        '--nt-texto': p.texto,
        '--nt-cinza': p.cinza,
        '--nt-rotulo': p.rotulo,
        '--nt-aba': p.aba,
        '--nt-borda': p.borda,
        '--nt-borda-forte': p.bordaForte,
        '--nt-apoio': p.apoio,
        '--nt-sobre-base': p.sobreBase,
        '--nt-branco': RESERVA_NT.branco,
        '--nt-sans': SANS_NORTE,
        '--nt-serifa': SERIFA_NORTE,
        // sombra curta dos cards (medida: 0 3px 4px -1px rgba(0,0,0,.1) + 0 0 1px)
        '--nt-sombra': `0 3px 4px -1px ${hexParaRgba(p.profundo, 0.1, 'rgba(0, 0, 0, ALPHA)')}, 0 0 0 1px ${hexParaRgba(p.profundo, 0.04, 'rgba(0, 0, 0, ALPHA)')}`,
        '--nt-sombra-alta': `0 12px 28px -8px ${hexParaRgba(p.profundo, 0.22, 'rgba(0, 0, 0, ALPHA)')}`,
        // véu do herói (a referência escurece a foto com um degradê do topo para o meio)
        '--nt-veu': hexParaRgba(p.profundo, 0.42, 'rgba(0, 0, 0, ALPHA)'),
        // largura útil medida: 1122px de conteúdo (159px de margem a 1440); 16px de lateral no celular
        '--nt-largura': '1122px',
        '--nt-lateral': '16px',
        '--nt-ease': 'cubic-bezier(0.22, 1, 0.36, 1)',
    } as CSSProperties;
};

/** Gate do modelo #13 "Norte" = topo 17 (mesmo critério de ehModeloLarissa/ehModeloInternacional). */
export const ehModeloNorte = (site: any): boolean => Number(site?.modelo_topo) === 17;

/**
 * Páginas internas no visual do modelo (/imoveis, ficha, /condominios, /empreendimento, /empresa, /blog): as vars
 * --nt-* e a sans do modelo na página toda (o globals.css aplica a fonte direto em div/p/a/span/button; redefinir
 * --fonte-principal na raiz é o mecanismo das peças). Página BRANCA como a referência. Fora do topo 17 ⇒ null.
 */
export const varsNorteInterna = (site: any): CSSProperties | null => {
    if (!ehModeloNorte(site)) return null;
    const p = paletaNorte(site);
    return {
        ...varsNorte(site),
        '--fonte-principal': SANS_NORTE,
        background: RESERVA_NT.branco,
        color: p.texto,
    } as CSSProperties;
};

// ---------------------------------------------------------------------------
// Imóvel: atributos no formato da referência "3 quartos | 2 banheiros | 120 m²" (separador vertical).
// ---------------------------------------------------------------------------

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/** Até 4 atributos com valor > 0 na ordem da referência (quartos, banheiros, área; vagas/suítes completam). */
export const atributosNorte = (slide: any, maximo = 4): string[] => {
    const n = (v: any) => Number(v) || 0;
    const itens: string[] = [];
    if (n(slide?.dormitorios) > 0) itens.push(plural(n(slide.dormitorios), 'quarto', 'quartos'));
    if (n(slide?.banheiros) > 0) itens.push(plural(n(slide.banheiros), 'banheiro', 'banheiros'));
    const area = slide?.area_privativa > 0 ? slide.area_privativa : slide?.area_total > 0 ? slide.area_total : slide?.terreno > 0 ? slide.terreno : 0;
    if (area > 0) itens.push(`${areaFormatada(Number(area))} ${slide?.area_privativa > 0 || slide?.area_total > 0 ? 'm²' : slide?.area_tipo || 'm²'}`);
    if (n(slide?.garagems) > 0) itens.push(plural(n(slide.garagems), 'vaga', 'vagas'));
    if (itens.length < maximo && n(slide?.suites) > 0) itens.push(plural(n(slide.suites), 'suíte', 'suítes'));
    return itens.slice(0, maximo);
};
